/**
 * Trip Templates Type Definitions
 * 
 * Defines all TypeScript interfaces and types for the trip templates feature.
 * Templates provide pre-built trip itineraries with estimated budgets that users
 * can customize and use as a starting point for their trips.
 */

import type { Timestamp } from 'firebase/firestore';
import type { ItineraryItemCategory } from '@/services/itinerary';
import type { TripDestination } from '@/services/trips';

/** Minimum itinerary days required to publish a trip template. */
export const MIN_TEMPLATE_ITINERARY_DAYS = 1;

/**
 * Difficulty levels for trip templates
 */
export type TemplateDifficulty = 'easy' | 'moderate' | 'adventurous';

/**
 * Travel style categories for filtering templates
 */
export type TravelStyle = 
  | 'cultural'
  | 'adventure'
  | 'relaxation'
  | 'food'
  | 'urban'
  | 'nature'
  | 'beach'
  | 'luxury'
  | 'budget'
  | 'family'
  | 'romantic'
  | 'solo'
  | 'road-trip'
  | 'safari';

/**
 * Template creator type
 */
export type TemplateCreator = 'system' | 'curator' | 'community';

/**
 * Template moderation status
 */
export type TemplateStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'archived';

/**
 * Template-specific activity categories (more descriptive than ItineraryItemCategory)
 * These are mapped to ItineraryItemCategory when creating actual itinerary items
 */
export type TemplateActivityCategory = 
  | 'lodging'
  | 'restaurant'
  | 'shopping'
  | 'attraction'
  | 'activity'
  | 'transport'
  | 'flight'
  | 'hotel'
  | 'other';

/**
 * Individual activity within a template day
 */
export type TemplateActivity = {
  time: string; // HH:mm format (e.g., "10:00")
  title: string;
  category: TemplateActivityCategory;
  description?: string;
  /**
   * Display-only venue/place label (e.g. "44th floor - Bisha Hotel").
   * This should not be appended to geocode queries.
   */
  venueName?: string;
  /**
   * Optional city/region hint for geocoding ambiguous address lines.
   * Example: "Toronto, Ontario" when address is short or PO box-like.
   */
  geocodeHint?: string;
  /**
   * Legacy field kept for backward compatibility with older templates.
   * New writes should prefer `geocodeHint`.
   */
  location?: string;
  address?: string;
  estimatedCost?: number; // In USD
  duration?: number; // Duration in minutes
  notes?: string;
};

/**
 * Day of itinerary within a template
 */
export type TemplateDay = {
  day: number; // Day number (1-indexed)
  title: string; // e.g., "Day 1: Arrival & Myeongdong"
  /**
   * Optional city for this day (used for deterministic multi-city templates).
   * Example: a Tokyo–Seoul template where day 5 starts in Seoul → day.city = "Seoul".
   */
  city?: string;
  /**
   * Optional country/region for this day (display + better geocoding context).
   */
  country?: string;
  description?: string;
  activities: TemplateActivity[];
};

/**
 * Budget breakdown structure
 */
export type TemplateBudget = {
  min: number;
  max: number;
  currency: string; // ISO currency code (e.g., "USD")
  breakdown: {
    flights: number;
    lodging: number;
    food: number;
    activities: number;
    transport: number;
    other?: number;
  };
};

/**
 * Complete trip template structure
 */
export type TripTemplate = {
  id: string;
  title: string; // e.g., "Seoul 7-Day Cultural Immersion"
  destination: string; // Primary destination (e.g., "Seoul, South Korea")
  destinations?: TripDestination[]; // For multi-city templates
  duration: number; // Number of days
  shortDescription: string; // Brief overview for card display
  longDescription?: string; // Detailed description for detail view
  coverImage?: string; // URL to cover image
  coverImageCredit?: string;
  coverImageCreditUrl?: string;
  difficulty: TemplateDifficulty;
  travelStyle: TravelStyle[]; // Multiple styles can apply
  estimatedBudget: TemplateBudget;
  itinerary: TemplateDay[];
  bestMonths: string[]; // e.g., ['Mar', 'Apr', 'May', 'Sep', 'Oct']
  tags: string[]; // e.g., ['solo-friendly', 'family-friendly', 'budget']
  highlights?: string[]; // Key highlights/selling points
  createdBy: TemplateCreator;
  featured: boolean;
  usageCount: number;
  rating?: number; // Average rating (0-5)
  reviewCount?: number;
  version?: number; // Template version for updates
  createdAt: Date;
  updatedAt: Date;
  // User-generated template fields
  status?: TemplateStatus; // Moderation status (default: 'pending' for user-generated)
  creatorId?: string; // User ID who created this template (for user-generated)
  creatorName?: string; // Display name of creator
  creatorAvatar?: string; // Avatar URL of creator
  creatorVerified?: boolean; // Whether creator is verified
  moderationNotes?: string; // Internal notes from moderators
  reviewedBy?: string; // User ID of reviewer
  reviewedAt?: Date; // When template was reviewed
  rejectionReason?: string; // Reason for rejection if status is 'rejected'
};

/**
 * Firestore version of TripTemplate (with Timestamp objects)
 */
export type FirestoreTripTemplate = Omit<TripTemplate, 'createdAt' | 'updatedAt' | 'destinations'> & {
  createdAt: Timestamp;
  updatedAt: Timestamp;
  destinations?: Array<Omit<TripDestination, 'startDate' | 'endDate'> & {
    startDate: Timestamp;
    endDate: Timestamp;
  }>;
};

/**
 * Template customization options when creating a trip
 */
export type TemplateCustomization = {
  startDate: Date;
  name?: string; // Override trip name
  budget?: number; // Override budget
  travelingFrom?: string;
};

/**
 * Filter options for template browsing
 */
export type TemplateFilter = {
  searchQuery?: string;
  destinations?: string[];
  minDuration?: number;
  maxDuration?: number;
  minBudget?: number;
  maxBudget?: number;
  difficulty?: TemplateDifficulty[];
  travelStyle?: TravelStyle[];
  tags?: string[];
  featuredOnly?: boolean;
  // User-generated template filters
  creatorId?: string; // Filter by creator
  status?: TemplateStatus[]; // Filter by status (only show approved for non-creators by default)
  includeDrafts?: boolean; // Include draft templates (only for creator's own templates)
  verifiedOnly?: boolean; // Only show templates from verified creators
};

/**
 * Sort options for template list
 */
export type TemplateSortBy = 
  | 'popular' // Sort by usageCount (descending)
  | 'newest' // Sort by createdAt (descending)
  | 'budget-low' // Sort by budget min (ascending)
  | 'budget-high' // Sort by budget max (descending)
  | 'duration-short' // Sort by duration (ascending)
  | 'duration-long' // Sort by duration (descending)
  | 'rating'; // Sort by rating (descending)

/**
 * Template usage analytics
 */
export type TemplateUsage = {
  templateId: string;
  userId: string;
  tripId: string; // ID of created trip
  customization: TemplateCustomization;
  createdAt: Date;
};

/**
 * Helper type for template statistics
 */
export type TemplateStats = {
  totalViews: number;
  totalUsage: number;
  conversionRate: number; // views to usage ratio
  averageRating: number;
  popularMonths: string[]; // Months when template is most used
};

